import React, { useState, useEffect, useRef } from 'react';
import styles from './DropdownList.module.scss';

type DropdownListProps = {
    title: string;
    width: string;
    options: { [key: string]: string };
    selected: string;
    onSelect: (value: string) => void;
    hasError?: boolean;
    isLimited?: boolean;
    disabled?: boolean;
    placeholder?: string;
};

const DROPDOWN_LIST_MAX_HEIGHT = 300;

const DropdownList: React.FC<DropdownListProps> = ({ title, width, options, selected, onSelect, hasError, isLimited, disabled, placeholder = 'Choisissez' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [selectedOption, setSelectedOption] = useState<{ key: string; value: string } | null>(null);

    useEffect(() => {
        if (options && selected && options[selected]) {
            setSelectedOption({ key: selected, value: options[selected] });
        }
    }, [options, selected]);

    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
        if (!isOpen && dropdownRef.current) {
            const { bottom, top } = dropdownRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - bottom;
            setOpenUpward(spaceBelow < DROPDOWN_LIST_MAX_HEIGHT && top > spaceBelow);
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (key: string) => {
        setSelectedOption({ key, value: options[key] });
        onSelect(key);
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.container}>
            <p className={styles.title}>{title}</p>
            <div className={styles.dropdown} style={{ width }} ref={dropdownRef}>
                <div
                    className={`${styles.dropdownHeader} ${hasError ? styles.errorBackground : ''}`}
                    style={{ width: isLimited ? '180px' : '100%', cursor: disabled ? 'unset' : 'pointer' }}
                    onClick={() => disabled ? null : handleToggle()}
                    tabIndex={0}
                >
                    {selectedOption ? <p className={styles.selectedOption}>{selectedOption.value}</p> : placeholder}
                    {!disabled && <span className={styles.arrow}>{isOpen ? '▲' : '▼'}</span>}
                </div>
                {isOpen && (
                    <ul className={`${styles.dropdownList} ${openUpward ? styles.openUpward : ''}`}>
                        {Object.entries(options).map(([key, value]) => (
                            <li
                                key={key}
                                className={`${styles.dropdownItem} ${selectedOption?.key === key ? styles.selected : ''}`}
                                onClick={() => handleSelect(key)}
                            >
                                {value}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default DropdownList;
